import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useFeed } from "../context/FeedContext";
import { pageColors } from "../mock";
import { ArrowLeft, Trash2, Flag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Chat = () => {
  const navigate = useNavigate();
  const { current, token } = useAuth();
  const { chat, postChat, refreshChat } = useFeed();
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState({});
  const endRef = useRef(null);

  useEffect(() => { document.title = "TMM|MEMBER CHAT"; }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat.length]);
  useEffect(() => {
    if (!current) return;
    const id = setInterval(refreshChat, 4000);
    return () => clearInterval(id);
  }, [current, refreshChat]);

  if (!current) {
    return (
      <>
        <Header hoverColor={pageColors.contact} compact={false} />
        <div className="gated-page">
          <h1>Members only</h1>
          <p>Chat is open to signed-in members.</p>
          <Link to="/members" className="btn-solid">Go to Members</Link>
        </div>
      </>
    );
  }

  const send = async (e) => {
    e.preventDefault();
    setErr("");
    if (!text.trim()) return;
    setBusy(true);
    const r = await postChat({ text });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setText("");
  };

  const softDelete = async (id) => {
    if (!window.confirm("Delete this message? It will show as '[deleted by author]' to everyone.")) return;
    await fetch(`${API}/api/feeds/chat/${id}`, {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    refreshChat();
  };

  const report = async (id) => {
    if (!window.confirm("Report this message to the mods?")) return;
    const reason = window.prompt("Optional: what's wrong with it?") || "";
    const r = await fetch(`${API}/api/feeds/chat/${id}/report`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ reason }),
    });
    if (r.ok) setReported((s) => ({ ...s, [id]: true }));
  };

  return (
    <>
      <Header hoverColor={pageColors.contact} compact={false} />
      <div className="chat-page">
        <header className="chat-header">
          <button
            type="button"
            className="feed-back"
            onClick={() => navigate("/members")}
          >
            <ArrowLeft size={16} /> Back to Members
          </button>
          <h1>Member Chat</h1>
          <span className="chat-note">Community chat — profanity and sensitive topics are auto-blocked. Report anything the filter misses.</span>
        </header>

        <div className="chat-scroll">
          {chat.length === 0 && <div className="feed-empty">No messages yet. Say hi.</div>}
          {chat.map((m) => {
            const mine = m.authorId === current.id;
            const deleted = m.deleted;
            return (
              <div key={m.id} className={`chat-bubble ${mine ? "mine" : ""} ${deleted ? "deleted" : ""}`}>
                <div className="chat-meta">
                  <strong>{mine ? "You" : m.authorName}</strong>
                  <span>{new Date(m.ts).toLocaleTimeString()}</span>
                </div>
                <div className="chat-text">{m.text}</div>
                {!deleted && (
                  <div className="chat-actions">
                    {mine ? (
                      <button
                        type="button"
                        className="chat-icon-btn"
                        onClick={() => softDelete(m.id)}
                        title="Delete for everyone"
                        data-testid={`chat-delete-${m.id}`}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    ) : reported[m.id] ? (
                      <span className="chat-icon-btn is-reported">Reported</span>
                    ) : (
                      <button
                        type="button"
                        className="chat-icon-btn"
                        onClick={() => report(m.id)}
                        title="Report to mods"
                        data-testid={`chat-report-${m.id}`}
                      >
                        <Flag size={13} /> Report
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form className="chat-composer" onSubmit={send}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Message the community…"
            data-testid="chat-input"
          />
          <button type="submit" className="btn-solid" disabled={busy} data-testid="chat-send">
            {busy ? "…" : "Send"}
          </button>
        </form>
        {err && <div className="auth-error" data-testid="chat-error" style={{ padding: "0.8rem 1.6rem" }}>{err}</div>}
      </div>
    </>
  );
};

export default Chat;
